import { useRef, useCallback, useEffect } from "react"

/**
 * Lightweight sound-effect hook.
 * Uses cloneNode() so rapid overlapping plays (e.g. wheel ticks) work.
 * Silently catches autoplay restrictions — by the time the user clicks
 * "Spin" the browser will have already unlocked audio.
 */
export function useSound(src: string, { volume = 1, preload = true } = {}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!preload) return
    const a = new Audio(src)
    a.preload = "auto"
    a.volume = volume
    audioRef.current = a
    return () => {
      a.pause()
      a.src = ""
    }
  }, [src, volume, preload])

  const play = useCallback(() => {
    try {
      const base = audioRef.current
      if (!base) {
        const a = new Audio(src)
        a.volume = volume
        audioRef.current = a
      }
      // Clone so overlapping plays don't cut each other off
      const clone = audioRef.current!.cloneNode(true) as HTMLAudioElement
      clone.volume = volume
      clone.play().catch(() => {})
    } catch {
      // Silently swallow — no audio is fine
    }
  }, [src, volume])

  const stop = useCallback(() => {
    audioRef.current?.pause()
    if (audioRef.current) audioRef.current.currentTime = 0
  }, [])

  return { play, stop }
}
